import styles from "./PresenceTooltip.module.scss";
import {
	faArrowLeft,
	faArrowRight,
	faCalendar,
	faCar,
	faCircleInfo,
	faHouse,
	faLocationPin,
	faMobileButton,
	faMugSaucer,
	faTreePalm
} from "web-fortawesome/npm/pro-light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useMemo, useState } from "react";
import { AsnPresenceStateFlags, EnumCallState } from "../../asn1/EUCSrv/stubs/ENetROSEInterface";
import { AsnAgentStateEnum } from "../../asn1/EUCSrv/stubs/ENetUC_Agent";
import { AsnContactPhoneLineState, AsnReachabilityEnum } from "../../asn1/EUCSrv/stubs/ENetUC_Common_AsnContact";
import { theSharedTranslationManager } from "../../helpers/helpers";
import { IContactContainer } from "../../interfaces/interfaces";
import { IKeyValuePair } from "../../interfaces/IKeyValuePair";
import { IConfigBlock } from "../../interfaces/interfacesConfig";
import { iPresenceStateToString } from "../userPresenceIcon/UserPresenceHelper";
import { EnumAppointmentSensitivity, EnumAppointmentState } from "../../asn1/EUCSrv/stubs/ENetUC_Common_Appointments";
import { IAsnAppointmentEntry } from "../../interfaces/serializable/IAsnAppointmentEntry";
import UserPresenceIcon from "../userPresenceIcon/UserPresenceIcon";
import { ClickAwayListener, List, ListItem, ListSubheader, Popover } from "@mui/material";
import PhoneNumberFormatHelper from "../../helpers/PhoneNumberFormatHelper";
import { IAppointmentEntryEX } from "../../interfaces/IAppointmentEntryEX";
import { IAsnLocationInformation2 } from "../../interfaces/serializable/IAsnLocationInformation2";
import { IAsnPresenceForward } from "../../interfaces/serializable/IAsnPresenceForward";
import { IAsnPresenceLine } from "../../interfaces/serializable/IAsnPresenceLine";
import UserPresenceHelper from "../../helpers/UserPresenceHelper";
import { PresenceIconPhone } from "../icons/PresenceIcons/PresenceIconPhone";
import { TPresenceIconType } from "../icons/PresenceIcons/IPresenceIconProps";
import { PresenceIconVideo } from "../icons/PresenceIcons/PresenceIconVideo";
import { PresenceIconAudio } from "../icons/PresenceIcons/PresenceIconAudio";
import { PresenceIconCalendar } from "../icons/PresenceIcons/PresenceIconCalendar";
import { PresenceIconChat } from "../icons/PresenceIcons/PresenceIconChat";
import { PresenceIconMeetings } from "../icons/PresenceIcons/PresenceIconMeetings";
import { PresenceIconTeams } from "../icons/PresenceIcons/PresenceIconTeams";
import { PresenceIconContactPortal } from "../icons/PresenceIcons/PresenceIconContactPortal";

interface IProps {
	close: () => void;
	configCD: IConfigBlock[];
	contact: IContactContainer | undefined;
	currentAppointments: IAppointmentEntryEX[];
	customLabels: IKeyValuePair[];
	nextAppointment: string;
	mouseIsOver?: (isOver: boolean) => void;
	// the location information of the selected line of the logged-in user
	// to compare against the listed phone numbers (needed to display the short version of a number)
	locationInformation?: IAsnLocationInformation2;
}

/**
 * The Presence ToolTip component
 */
// eslint-disable-next-line react/display-name
export const PresenceTooltip = React.forwardRef<HTMLElement, IProps>((props: IProps, ref) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const nextValidAppointment = props.contact?.nextAppointment !== undefined && props.contact.nextAppointment.bIsValid ? props.contact.nextAppointment : undefined;
	const [teamsStatusText, setTeamsStatusText] = useState<string>("");

	useEffect(() => {
		if (ref) {
			const x = ref as React.MutableRefObject<HTMLDivElement>;
			setAnchorEl(x.current);
		}
	}, [ref]);

	/**
	 * Get IDS label, used for several checks
	 */
	const presenceLabel = useMemo(() => {
		return iPresenceStateToString(props.contact?.presence, true);
	}, [props.contact?.presence]);

	/**
	 * Contact name
	 */
	const contactName = useMemo(() => {
		if (!props.contact)
			return "";
		let name: string | undefined;
		if (props.contact?.asnNetDatabaseContact === undefined)
			name = theSharedTranslationManager.getTranslation("IDS_UNKNOWN_USER");
		else
			name = props.contact?.asnNetDatabaseContact?.u8sDisplayName;
		return name ?? "";
	}, [props.contact]);

	/**
	 * Phone lines
	 */
	const seqPhoneLines = useMemo(() => {
		return props.contact?.seqPhoneLines ?? [];
	}, [props.contact?.seqPhoneLines]);

	const tapiLines = seqPhoneLines.filter(
		item => item.u8sLinePhoneNumber.includes("phys=") &&
			!item.u8sLinePhoneNumber.includes("phys=ClientAV-") &&
			!item.u8sLinePhoneNumber.includes("phys=SIPAV-") &&
				!item.u8sLinePhoneNumber.includes("phys=Mobile"));
	const sipLines = seqPhoneLines.filter(item => item.u8sLinePhoneNumber.includes("phys=SIPAV-"));
	const avLines = seqPhoneLines.filter(item => item.u8sLinePhoneNumber.includes("phys=ClientAV-"));
	const mobileLines = seqPhoneLines.filter(item => item.u8sLinePhoneNumber.includes("phys=Mobile"));

	/**
	 * Active or ringing call, needed to know the priority
	 */
	const activeOrRingingCall = useMemo(() => {
		if (!props.contact?.seqCalls || !props.contact?.seqCalls.length)
			return null;
		// Priority to connected call
		const connectedCall = props.contact?.seqCalls.find(item => item.iCallState === EnumCallState.eCALLSTATECONNECTED);
		const ringingOutBound = props.contact?.seqCalls.find(item => item.iCallState === EnumCallState.eCALLSTATERINGBACK);
		const ringingInBound = props.contact?.seqCalls.find(item => item.iCallState === EnumCallState.eCALLSTATEOFFERING);
		const call = connectedCall || ringingOutBound || ringingInBound;
		if (!call)
			return null;
		return call;
	}, [props.contact?.seqCalls]);

	const reachability = useMemo(() => {
		return props.contact?.absentState?.iReachability;
	}, [props.contact?.absentState?.iReachability]);

	const getNextAppointmentSubject = () => {
		let nextAppointmentSubject = nextValidAppointment?.u8sSubject;

		if (nextValidAppointment && nextValidAppointment.iSensitivity) {
			switch (nextValidAppointment.iSensitivity) {
				case EnumAppointmentSensitivity.eAPPOINTMENTSENSITIVITYNORMAL:
					break;
				case EnumAppointmentSensitivity.eAPPOINTMENTSENSITIVITYPRIVATE:
				case EnumAppointmentSensitivity.eAPPOINTMENTSENSITIVITYCONFIDENTIAL:
				case EnumAppointmentSensitivity.eAPPOINTMENTSENSITIVITYPERSONAL:
					nextAppointmentSubject = "(" + theSharedTranslationManager.getTranslation("IDS_APPOINTMENT_SENSITIVITY_PRIVATE") + ")";
					break;
				default:
					break;
			}
		}
		return nextAppointmentSubject;
	};

	const getCurrentAppointmentSubject = (appointment: IAsnAppointmentEntry) => {
		let appointmentSubject = appointment?.u8sSubject;

		if (appointment && appointment.iSensitivity) {
			switch (appointment.iSensitivity) {
				case EnumAppointmentSensitivity.eAPPOINTMENTSENSITIVITYNORMAL:
					break;
				case EnumAppointmentSensitivity.eAPPOINTMENTSENSITIVITYPRIVATE:
				case EnumAppointmentSensitivity.eAPPOINTMENTSENSITIVITYCONFIDENTIAL:
				case EnumAppointmentSensitivity.eAPPOINTMENTSENSITIVITYPERSONAL:
					appointmentSubject = "(" + theSharedTranslationManager.getTranslation("IDS_APPOINTMENT_SENSITIVITY_PRIVATE") + ")";
					break;
				default:
					break;
			}
		}
		return appointmentSubject;
	};

	const getCalendarStatus = (appointmentStatus: EnumAppointmentState) => {
		switch (appointmentStatus) {
			case EnumAppointmentState.eAPPOINTMENTSTATUSBUSY:
				return "busy";
			case EnumAppointmentState.eAPPOINTMENTSTATUSOUTOFOFFICE:
				return "absent";
			default: // eAPPOINTMENTSTATUSFREE
				return "free";
		}
	};

	const getNextAppointmentCalendarStatus = () => {
		let calendarStatus = "";

		if (nextValidAppointment && nextValidAppointment.iStatus) {
			switch (nextValidAppointment.iStatus) {
				case EnumAppointmentState.eAPPOINTMENTSTATUSBUSY:
					calendarStatus = styles.iconBusy;
					break;
				case EnumAppointmentState.eAPPOINTMENTSTATUSOUTOFOFFICE:
					calendarStatus = styles.iconAbsent;
					break;
				default:
					break;
			}
		}
		return calendarStatus;
	};

	const teamsAvailable = useMemo(() => {
		return !!props.contact?.msTeamsEmail?.email;
	}, [props.contact?.msTeamsEmail]);

	/**
	 * Helper to get the teams icon type for the PresenceIconTeams
	 * @returns - the icon type
	 */
	const getTeamsIconType = (): TPresenceIconType => {
		switch (iPresenceStateToString(props?.contact?.msTeamsEmail?.value)) {
			case "busy":
			case "dnd":
				return "busy";
			case "absent":
				return "absent";
			case "unavailable":
				return "offline";
			case "available":
			default:
				return "free";
		}
	};

	const getReachabilityTypeIcon = () => {
		let iconDefinition = faMugSaucer;

		if (reachability === AsnReachabilityEnum.eREACHABILITYBUSINESSTRIP)
			iconDefinition = faCar;
		else if (reachability === AsnReachabilityEnum.eREACHABILITYHOMEOFFICE)
			iconDefinition = faHouse;
		else if (reachability === AsnReachabilityEnum.eREACHABILITYVACATION)
			iconDefinition = faTreePalm;

		return iconDefinition;
	};

	const getReachabilityText = () => {
		let reachabilityText = "";

		if (reachability === AsnReachabilityEnum.eREACHABILITYPAUSE)
			reachabilityText = theSharedTranslationManager.getTranslation("IDS_REACHABILITY_PAUSE");
		else if (reachability === AsnReachabilityEnum.eREACHABILITYBUSINESSTRIP)
			reachabilityText = theSharedTranslationManager.getTranslation("IDS_REACHABILITY_BUSINESS_TRIP");
		else if (reachability === AsnReachabilityEnum.eREACHABILITYHOMEOFFICE)
			reachabilityText = theSharedTranslationManager.getTranslation("IDS_REACHABILITY_REMOTE_WORKING");
		else if (reachability === AsnReachabilityEnum.eREACHABILITYVACATION)
			reachabilityText = theSharedTranslationManager.getTranslation("IDS_REACHABILITY_VACATION");

		return reachabilityText;
	};

	/**
	 * Helper to get the teams icon type for the PresenceIconContactPortal
	 * @returns - the icon type
	 */
	const getAgentStateIconType = (): TPresenceIconType => {
		switch (props.contact?.agentState) {
			case AsnAgentStateEnum.eAGENTSTATEBUSY:
				return "busy";
			case AsnAgentStateEnum.eAGENTSTATENOTREADY:
				return "offline";
			case AsnAgentStateEnum.eAGENTSTATEREADY:
			default:
				return "free";
		}
	};

	const getAgentStateText = () => {
		let agentStateText = "";
		if (props.contact?.agentState === AsnAgentStateEnum.eAGENTSTATEREADY)
			agentStateText = theSharedTranslationManager.getTranslation("IDS_AGENT_STATE_READY");
		else if (props.contact?.agentState === AsnAgentStateEnum.eAGENTSTATEBUSY)
			agentStateText = theSharedTranslationManager.getTranslation("IDS_AGENT_STATE_BUSY");
		else if (props.contact?.agentState === AsnAgentStateEnum.eAGENTSTATENOTREADY)
			agentStateText = theSharedTranslationManager.getTranslation("IDS_AGENT_STATE_NOTREADY");
		else if (props.contact?.agentState === AsnAgentStateEnum.eAGENTSTATEWORKINGAFTERCALL)
			agentStateText = theSharedTranslationManager.getTranslation("IDS_AGENT_STATE_WORKINGAFTERCALL");

		return agentStateText;
	};

	/**
	 * Helper to get the current state of the forwarded line and return the icon type according to it
	 * @param line - the forwarded line
	 * @returns - a TPresenceIconType
	 */
	const getForwardedLineType = (line: IAsnPresenceForward): TPresenceIconType => {
		const findLine = seqPhoneLines.find(item => item.u8sLinePhoneNumber === line.u8sLinePhoneNumber);
		if (findLine?.iState === AsnContactPhoneLineState.eLINEOFFLINE)
			return "offline";
		return activeOrRingingCall?.u8sLinePhoneNumber === line.u8sLinePhoneNumber ? "busy" : "free";
	};

	/**
	 * Helper to get the current state of a line and return the icon type according to it
	 * @param line - the line
	 * @returns - a TPresenceIconType
	 */
	const getLineType = (line: IAsnPresenceLine): TPresenceIconType => {
		if (line.iState === AsnContactPhoneLineState.eLINEOFFLINE)
			return "offline";
		return activeOrRingingCall?.u8sLinePhoneNumber === line.u8sLinePhoneNumber ? "busy" : "free";
	};

	/**
	 * Render the correct line information according to the type
	 * @param phoneLine - the phone line
	 * @param index - index is used to the React key
	 * @param type - the line type
	 * @returns - the React element(s)
	 */
	const getPhoneLineChannelSection = (phoneLine: IAsnPresenceLine, index: number, type: "SIP" | "Tapi" | "AV" | "Mobile") => {
		const div: React.ReactElement[] = [];
		const phoneNumber = PhoneNumberFormatHelper.getPhoneNumber(phoneLine.u8sLineNumber);
		switch (type) {
			case "SIP":
			case "Tapi":
				div.push(<ListItem
					key={`line_${type}-${index}`}
					role="listitem"
					className={styles.item}
				>
					<div className={styles.leftIcon}>
						<PresenceIconPhone
							type={getLineType(phoneLine)}
							callProtection={phoneLine.iDoNotDisturb === 1}
							lineForwarded={props.contact?.seqLineForwards?.find(line => line.u8sLinePhoneNumber === phoneLine.u8sLinePhoneNumber) !== undefined}
						/>
					</div>
					<p
						aria-description={theSharedTranslationManager.getTranslation("IDS_JOURNAL_LINE")}
						className={styles.small}
					>
						[{PhoneNumberFormatHelper.getPhoneNumberInternal(
							PhoneNumberFormatHelper.getPhoneNumber(phoneLine.u8sLineNumber),
							props.locationInformation
						)}]&nbsp;
						{phoneLine.iDoNotDisturb ? " " + theSharedTranslationManager.getTranslation("IDS_CHANNEL_CALL_PROTECTION") : ""}
					</p>
				</ListItem>);
				break;
			case "AV":
				if (props?.contact?.clientCapabilities?.handleAudio) {
					div.push(<ListItem key={`line_a-${type}-${index}`} role="listitem" className={styles.item}>
						<div className={styles.leftIcon}>
							<PresenceIconAudio
								type={getLineType(phoneLine)}
								callProtection={phoneLine.iDoNotDisturb === 1}
							/>
						</div>
						<p className={styles.small}>
							{[theSharedTranslationManager.getTranslation("IDS_CHAT_MSG_AUDIOCHAT")]}
						</p>
					</ListItem>);
				}
				if (props?.contact?.clientCapabilities?.handleVideo) {
					div.push(<ListItem key={`line_v-${type}-${index}`} role="listitem" className={styles.item}>
						<div className={styles.leftIcon}>
							<PresenceIconVideo
								type={getLineType(phoneLine)}
								callProtection={phoneLine.iDoNotDisturb === 1}
							/>
						</div>
						<p className={styles.small}>
							{[theSharedTranslationManager.getTranslation("IDS_CHAT_MSG_VIDEOCHAT")]}
						</p>
					</ListItem>);
				}
				break;
			case "Mobile":
				div.push(
					<ListItem key={`line_${type}-${index}`} role="listitem" className={styles.item}>
						<div className={styles.leftIcon}>
							<PresenceIconPhone type={phoneLine.iState === 1 ? "offline" : "free"} />
						</div>
						<p className={styles.small}>
							`[${phoneNumber.toDisplay}]${phoneLine.iState === 1 ? (" " + theSharedTranslationManager.getTranslation("IDS_LINE_OFFLINE")) : ""}`
						</p>
					</ListItem>
				);
				break;
			default:
				break;
		}
		return div.length ? div : null;
	};

	useEffect(() => {
		let message = "";
		if (props.contact?.presence) {
			if (props.contact?.presence & AsnPresenceStateFlags.eCALL)
				message = theSharedTranslationManager.getTranslation("IDS_CHANNEL_CONVERSATION");

			else if (props.contact?.presence & AsnPresenceStateFlags.eCONFERENCING)
				message = theSharedTranslationManager.getTranslation("IDS_CHANNEL_MEETING");
		}

		if (props.contact?.msTeamsEmail && message === "") {
			switch (iPresenceStateToString(props.contact.msTeamsEmail?.value)) {
				case "available":
					message = theSharedTranslationManager.getTranslation("IDS_PRESENCE_AVAILABLE");
					break;
				case "busy":
					message = theSharedTranslationManager.getTranslation("IDS_PRESENCE_BUSY");
					break;
				case "absent":
					message = theSharedTranslationManager.getTranslation("IDS_PRESENCE_ABSENT");
					break;
				case "dnd":
					message = theSharedTranslationManager.getTranslation("IDS_PRESENCE_DND");
					break;
				case "unavailable":
					message = theSharedTranslationManager.getTranslation("IDS_PRESENCE_OFFLINE");
					break;
				default:
					message = theSharedTranslationManager.getTranslation("");
					break;
			}
		}
		setTeamsStatusText(message);
	}, [props.contact?.msTeamsEmail, props.contact?.presence]);

	/**
	 * Status text only for meeting, call connected or ringing.
	 * Empty string for another logic.
	 */
	const callOrMeetingStatusText = useMemo(() => {
		if (activeOrRingingCall && activeOrRingingCall.iCallState === EnumCallState.eCALLSTATECONNECTED)
			return theSharedTranslationManager.getTranslation("IDS_CALL_CONNECTED");
		else {
			if (presenceLabel === "IDS_HAS_MEETING" || presenceLabel === "IDS_CALL_CONNECTED" || presenceLabel === "IDS_CALL_RINGING")
				return theSharedTranslationManager.getTranslation(presenceLabel);
		}
		return "";
	}, [presenceLabel, activeOrRingingCall]);

	/**
	 * Display status text for "in a call"
	 */
	const inACall = useMemo(() => {
		if (!activeOrRingingCall)
			return null;
		const phoneNumber = PhoneNumberFormatHelper.getPhoneNumber(activeOrRingingCall.u8sCurrentNumberSC);
		return (
			<>
				{!activeOrRingingCall.iOutbound
					? <FontAwesomeIcon className={styles.arrow} transform={"shrink-1"} icon={faArrowLeft} />
					: <FontAwesomeIcon className={styles.arrow} transform={"shrink-1"} icon={faArrowRight} />}
				<p className={styles.small}>
					{" " + (activeOrRingingCall.remoteContact?.u8sDisplayName ?? phoneNumber.toDisplay)}
				</p>
			</>
		);
	}, [activeOrRingingCall]);

	const handleKeyDown = (event: React.KeyboardEvent): void => {
		if (event.key === "Escape" ||
			event.key === "Tab" ||
			event.key === "Enter" ||
			event.code === "Space" ||
			event.key === "ArrowRight" ||
			event.key === "ArrowLeft" ||
			event.key === "ArrowUp" ||
			event.key === "ArrowDown"
		) {
			event.preventDefault();
			event.stopPropagation();
			props.close();
		}
	};

	return (
		<ClickAwayListener onClickAway={() => { props.close(); }}>
			<Popover
				style={{ pointerEvents: "none" }}
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={() => { props.close(); }}
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					props.close();
				}}
				onMouseEnter={() => { props.mouseIsOver && props.mouseIsOver(true); }}
				onMouseLeave={() => { props.mouseIsOver && props.mouseIsOver(false); }}
				onKeyDown={handleKeyDown}
			>
				<List sx={{ paddingTop: 0, border: "1px solid transparent", borderRadius: "5px", boxSizing: "border-box" }} className={styles.list}>
					<ListSubheader disableSticky className={`${styles.item}`}>
						<h2 className={styles.header}>{contactName}</h2>
					</ListSubheader>
					<ListItem role="listitem" className={styles.item}>
						<div className={styles.leftIcon}>
							<UserPresenceIcon pict={null} pres={props.contact?.presence ?? 0} size={12} />
						</div>
						<p className={styles.subTitle}>
							{theSharedTranslationManager.getTranslation(iPresenceStateToString(props.contact?.presence, true))}
						</p>
					</ListItem>
					{props.contact?.presence
						? <ListItem role="listitem" className={styles.item}>
							<p className={styles.small}>
								{callOrMeetingStatusText !== "" ? callOrMeetingStatusText + " " : theSharedTranslationManager.getTranslation("IDS_APPOINTMENT_HEADER") + " " + "(" + theSharedTranslationManager.getTranslation(iPresenceStateToString(props.contact?.presence, true)) + ")"}
							</p>
							{inACall}
						</ListItem>
						: null
					}
					{props.contact?.presence
						? <ListSubheader disableSticky className={`${styles.item} ${styles.subHeader}`}>
							<h3 className={styles.subTitle}>
								{theSharedTranslationManager.getTranslation("IDS_INFORMATION_HEADER")}
							</h3>
						</ListSubheader>
						: null
					}
					{props.contact?.presence && !!(props.contact.presence & AsnPresenceStateFlags.eMOBILEAVAILABLITY)
						? <ListItem role="listitem" className={styles.item}>
							<div className={styles.leftIcon}>
								<FontAwesomeIcon
									transform={"shrink-1"}
									icon={faMobileButton}
								/>
							</div>
							<p className={styles.small}>
								{theSharedTranslationManager.getTranslation("IDS_STATE_AVAILABLE_MOBILE")}
							</p>
						</ListItem>
						: null
					}
					{(props.contact?.agentState !== AsnAgentStateEnum.eAGENTSTATENOAGENT)
						? <ListItem role="listitem" className={styles.item}>
							<div className={styles.leftIcon}>
								<PresenceIconContactPortal type={getAgentStateIconType()} />
							</div>
							<p className={styles.small}>
								{getAgentStateText()}
							</p>
						</ListItem>
						: null
					}
					{(reachability !== undefined && reachability !== AsnReachabilityEnum.eREACHABILITYNORMAL)
						? <ListItem role="listitem" className={styles.item}>
							<div className={styles.leftIcon}>
								<FontAwesomeIcon
									transform={"shrink-1"}
									icon={getReachabilityTypeIcon()}
								/>
							</div>
							<p className={styles.small}>
								{getReachabilityText()}
							</p>
						</ListItem>
						: null
					}
					{props.contact?.customNote
						? <ListItem role="listitem" className={styles.item}>
							<div className={styles.leftIcon}>
								<FontAwesomeIcon
									transform={"shrink-1"}
									icon={faCircleInfo}
								/>
							</div>
							<p className={styles.small}>
								{props.contact.customNote}
							</p>
						</ListItem>
						: null
					}
					{reachability !== AsnReachabilityEnum.eREACHABILITYVACATION
						? <ListSubheader disableSticky className={`${styles.item} ${styles.subHeader}`}>
							<h3 className={styles.subTitle}>
								{theSharedTranslationManager.getTranslation("IDS_CHANNELS_HEADER")}
							</h3>
						</ListSubheader>
						: null
					}
					{reachability !== AsnReachabilityEnum.eREACHABILITYVACATION
						? props.contact?.seqLineForwards?.map((lineForward: IAsnPresenceForward, index: number) => (
							<ListItem key={index} role="listitem" className={styles.item}>
								<div className={styles.leftIcon}>
									<PresenceIconPhone
										type={getForwardedLineType(lineForward)}
										lineForwarded
									/>
								</div>
								<p className={styles.small}>
									[{PhoneNumberFormatHelper.getPhoneNumberInternal(
										PhoneNumberFormatHelper.getPhoneNumber(lineForward.u8sLineNumber),
										props.locationInformation
									)}]&nbsp;
									{theSharedTranslationManager.getTranslation(UserPresenceHelper.getChannelCallForwardLabelByForwardMode(lineForward.iForwardMode))}&nbsp;
									[{PhoneNumberFormatHelper.getPhoneNumberInternal(
										PhoneNumberFormatHelper.getPhoneNumber(lineForward.u8sDestination),
										props.locationInformation
									)}]
								</p>
							</ListItem>
						))
						: null
					}
					{reachability !== AsnReachabilityEnum.eREACHABILITYVACATION
						? <>
							{sipLines.length
								? sipLines.map((phoneLine: IAsnPresenceLine, index: number) => (
									getPhoneLineChannelSection(phoneLine, index, "SIP")
								))
								: null
							}
							{tapiLines.length
								? tapiLines.map((phoneLine: IAsnPresenceLine, index: number) => (
									getPhoneLineChannelSection(phoneLine, index, "Tapi")
								))
								: null
							}
							{avLines.length
								? avLines.map((phoneLine: IAsnPresenceLine, index: number) => (
									getPhoneLineChannelSection(phoneLine, index, "AV")
								))
								: null
							}
							{mobileLines.length
								? mobileLines.map((phoneLine: IAsnPresenceLine, index: number) => (
									getPhoneLineChannelSection(phoneLine, index, "Mobile")
								))
								: null
							}
						</>
						: null
					}
					{reachability !== AsnReachabilityEnum.eREACHABILITYVACATION && callOrMeetingStatusText === theSharedTranslationManager.getTranslation("IDS_HAS_MEETING")
						? <ListItem role="listitem" className={styles.item}>
							<div className={styles.leftIcon}>
								<PresenceIconMeetings type={"busy"} />
							</div>
							<p className={styles.small}>
								{theSharedTranslationManager.getTranslation("IDS_HAS_MEETING")}
							</p>
						</ListItem>
						: null
					}
					{reachability !== AsnReachabilityEnum.eREACHABILITYVACATION && teamsAvailable
						? <ListItem role="listitem" className={styles.item}>
							<div className={styles.leftIcon}>
								<PresenceIconTeams
									type={getTeamsIconType()}
									callProtection={iPresenceStateToString(props?.contact?.msTeamsEmail?.value) === "dnd"}
								/>
							</div>
							<p className={styles.small}>
								{teamsStatusText}
							</p>
						</ListItem>
						: null
					}
					{props.currentAppointments?.length
						? <ListSubheader disableSticky className={`${styles.item} ${styles.subHeader}`}>
							<h3 className={styles.subTitle}>
								{theSharedTranslationManager.getTranslation("IDS_APPOINTMENT_HEADER")}
							</h3>
						</ListSubheader>
						: null
					}
					{props.currentAppointments?.map((appointment, index) => {
						return (
							<React.Fragment key={`ap_${index}`}>
								<ListItem role="listitem" className={`${styles.item} ${styles.topMargin}`}>
									{appointment.enrichedDescription !== theSharedTranslationManager.getTranslation("IDS_APPOINTMENT_NO_NEXT24HRS")
										? <div className={styles.leftIcon}>
											<PresenceIconCalendar type={getCalendarStatus(appointment.iStatus)} />
										</div>
										: null
									}
									<p className={styles.small}>
										{appointment.enrichedDescription}
									</p>
								</ListItem>
								{getCurrentAppointmentSubject(appointment) && getCurrentAppointmentSubject(appointment) !== ""
									? <ListItem role="listitem" className={styles.item}>
										<div className={styles.leftIcon}>
											<PresenceIconChat />
										</div>
										<p className={styles.small}>
											{getCurrentAppointmentSubject(appointment)}
										</p>
									</ListItem>
									: null
								}
								{appointment.Location
									? <ListItem role="listitem" className={styles.item}>
										<div className={styles.leftIcon}>
											<FontAwesomeIcon
												transform={"shrink-1"}
												icon={faLocationPin}
											/>
										</div>
										<p className={styles.small}>
											{appointment.Location}
										</p>
									</ListItem>
									: null
								}
							</React.Fragment>
						);
					})}
					{nextValidAppointment?.stStart
						? <ListSubheader disableSticky className={`${styles.item} ${styles.subHeader}`}>
							<h3 className={styles.subTitle}>
								{theSharedTranslationManager.getTranslation("IDS_APPOINTMENT_NEXT")}
							</h3>
						</ListSubheader>
						: null
					}
					{nextValidAppointment?.stStart
						? <ListItem role="listitem" className={`${styles.item} ${styles.topMargin}`}>
							{props.nextAppointment !== theSharedTranslationManager.getTranslation("IDS_APPOINTMENT_NO_FURTHER_NEXT24HRS") && props.nextAppointment !== theSharedTranslationManager.getTranslation("IDS_APPOINTMENT_NO_NEXT24HRS")
								? <div className={styles.leftIcon}>
									<FontAwesomeIcon
										className={getNextAppointmentCalendarStatus()}
										transform={"shrink-1"}
										icon={faCalendar} />
								</div>
								: null
							}
							<p className={styles.small}>
								{props.nextAppointment}
							</p>
						</ListItem>
						: null
					}
					{nextValidAppointment?.stStart && props.nextAppointment !== theSharedTranslationManager.getTranslation("IDS_APPOINTMENT_NO_FURTHER_NEXT24HRS") && props.nextAppointment !== theSharedTranslationManager.getTranslation("IDS_APPOINTMENT_NO_NEXT24HRS")
						? <>
							{getNextAppointmentSubject() && getNextAppointmentSubject() !== ""
								? <ListItem role="listitem" className={`${styles.item}`}>
									<div className={styles.leftIcon}>
										<PresenceIconChat />
									</div>
									<p className={styles.small}>
										{getNextAppointmentSubject()}
									</p>
								</ListItem>
								: null
							}
							{nextValidAppointment?.Location
								? <ListItem role="listitem" className={styles.item}>
									<div className={styles.leftIcon}>
										<FontAwesomeIcon
											transform={"shrink-1"}
											icon={faLocationPin}
										/>
									</div>
									<p className={styles.small}>
										{nextValidAppointment.Location}
									</p>
								</ListItem>
								: null
							}
						</>
						: null
					}
				</List>
			</Popover>
		</ClickAwayListener>
	);
});
